import { useEffect, useRef, useState } from 'react'

import { getContractDocumentSendProgress, startSendedContractDocuments } from '@api/contracts/api'
import type { IContractDocumentSendJob } from '@api/contracts/state.types'

import { CardContractSendProgress } from './ui'
import type { ICardContractSendProgressContainer } from './types'


const pollIntervalMs = 700
const maxConsecutivePollFailures = 3

const createInitialJob = (): IContractDocumentSendJob => ({
    jobID: '',
    status: 'queued',
    progress: 0,
    processedFiles: 0,
    totalFiles: 0,
    sentFiles: 0,
    failedFiles: 0,
    message: 'Подготавливаю отправку документов...',
    files: [],
})

const createClientFailureJob = (job: IContractDocumentSendJob, errorMessage: string): IContractDocumentSendJob => ({
    ...job,
    status: 'failed',
    progress: 100,
    message: 'Не удалось получить результат отправки.',
    errorMessage,
})

export const CardContractSendProgressContainer = ({
    isOpen,
    contractID,
    handlerFinished,
    handlerButtonCancel,
}: ICardContractSendProgressContainer) => {
    const [job, setJob] = useState<IContractDocumentSendJob>(createInitialJob)
    const finishedHandlerRef = useRef(handlerFinished)

    useEffect(() => {
        finishedHandlerRef.current = handlerFinished
    }, [handlerFinished])

    useEffect(() => {
        if (!isOpen || contractID <= 0) return

        let isCancelled = false
        let didFinish = false
        let pollTimer: ReturnType<typeof setTimeout> | undefined

        const waitForNextPoll = () => new Promise<void>((resolve) => {
            pollTimer = setTimeout(resolve, pollIntervalMs)
        })

        const finish = (finishedJob: IContractDocumentSendJob) => {
            if (isCancelled || didFinish) return

            didFinish = true
            setJob(finishedJob)
            finishedHandlerRef.current?.(finishedJob)
        }

        const run = async () => {
            let currentJob = createInitialJob()
            setJob(currentJob)

            const startResponse = await startSendedContractDocuments(contractID)
            if (isCancelled) return

            const jobID = startResponse.data?.jobID
            if (startResponse.status !== 200 || !jobID) {
                finish(createClientFailureJob(currentJob, startResponse.message || 'Не удалось запустить отправку документов.'))
                return
            }

            currentJob = {...currentJob, jobID}
            setJob(currentJob)

            let consecutivePollFailures = 0
            while (!isCancelled) {
                const progressResponse = await getContractDocumentSendProgress(jobID)
                if (isCancelled) return

                const nextJob = progressResponse.data?.job
                if (progressResponse.status !== 200 || !nextJob) {
                    consecutivePollFailures += 1
                    if (consecutivePollFailures >= maxConsecutivePollFailures) {
                        finish(createClientFailureJob(
                            currentJob,
                            'Не удалось получить актуальный статус. Отправка на сервере могла продолжиться.',
                        ))
                        return
                    }

                    await waitForNextPoll()
                    continue
                }

                consecutivePollFailures = 0
                currentJob = nextJob
                setJob(nextJob)

                if (nextJob.status === 'completed' || nextJob.status === 'failed') {
                    finish(nextJob)
                    return
                }

                await waitForNextPoll()
            }
        }

        void run()

        return () => {
            isCancelled = true
            if (pollTimer) clearTimeout(pollTimer)
        }
    }, [contractID, isOpen])

    return <CardContractSendProgress
        isOpen={isOpen}
        job={job}
        handlerButtonCancel={handlerButtonCancel}
    />
}